"use client";

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { SendIcon } from "lucide-react";
import { useRouter } from "@/i18n/navigation";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { submitContactMessage } from "./actions";
import {
	contactFormSchema,
	type ContactFormValues,
} from "./contact-schema";

type ContactFormProps = {
	isPt: boolean;
	locale: string;
};

export function ContactForm({ isPt, locale }: ContactFormProps) {
	const router = useRouter();
	const [submitError, setSubmitError] = useState<string | null>(null);
	const form = useForm<ContactFormValues>({
		resolver: zodResolver(contactFormSchema),
		defaultValues: {
			name: "",
			email: "",
			message: "",
			website: "",
		},
	});

	async function onSubmit(data: ContactFormValues) {
		setSubmitError(null);
		const result = await submitContactMessage({
			...data,
			locale,
		});

		if (!result.success) {
			setSubmitError(
				result.error ??
					(isPt
						? "Nao foi possivel enviar sua mensagem."
						: "Could not send your message."),
			);
			return;
		}

		router.replace("/?contact=sent");
	}

	return (
		<form
			id="contact-form"
			className="space-y-5"
			onSubmit={form.handleSubmit(onSubmit)}
		>
			<input
				{...form.register("website")}
				tabIndex={-1}
				autoComplete="off"
				aria-hidden
				className="absolute left-[-9999px] top-[-9999px]"
			/>

			<FieldGroup>
				<Controller
					name="name"
					control={form.control}
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor="contact-form-name">
								{isPt ? "Nome" : "Name"}
							</FieldLabel>
							<Input
								{...field}
								id="contact-form-name"
								aria-invalid={fieldState.invalid}
								autoComplete="name"
							/>
							{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
						</Field>
					)}
				/>

				<Controller
					name="email"
					control={form.control}
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor="contact-form-email">
								{isPt ? "E-mail" : "Email"}
							</FieldLabel>
							<Input
								{...field}
								id="contact-form-email"
								type="email"
								aria-invalid={fieldState.invalid}
								autoComplete="email"
							/>
							{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
						</Field>
					)}
				/>

				<Controller
					name="message"
					control={form.control}
					render={({ field, fieldState }) => (
						<Field data-invalid={fieldState.invalid}>
							<FieldLabel htmlFor="contact-form-message">
								{isPt ? "Mensagem" : "Message"}
							</FieldLabel>
							<Textarea
								{...field}
								id="contact-form-message"
								aria-invalid={fieldState.invalid}
								className="min-h-36"
							/>
							{fieldState.invalid && <FieldError errors={[fieldState.error]} />}
						</Field>
					)}
				/>
			</FieldGroup>

			{submitError ? <FieldError>{submitError}</FieldError> : null}

			<div className="flex justify-end">
				<Button type="submit" disabled={form.formState.isSubmitting}>
					<SendIcon className="h-4 w-4" />
					{form.formState.isSubmitting
						? isPt
							? "Enviando..."
							: "Sending..."
						: isPt
							? "Enviar"
							: "Send"}
				</Button>
			</div>

		</form>
	);
}
